import { graphql } from '../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../query/hooks';
import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ModalActions } from '../../../../modal/components/ModalActions';
import { ModalContent } from '../../../../modal/components/ModalContent';
import { FormField } from '../../../../form/FormField';
import { useModalContext } from '../../../../modal/ModalContext';
import { z } from 'zod';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../../store/snackbar';

const deleteTeamMutation = graphql(`
	mutation organization_deleteTeam($teamId: UUID!) {
		deleteTeam(by: { id: $teamId }) {
			ok
			errorMessage
		}
	}
`);

const getTeamQuery = graphql(`
	query organization_getTeamById($id: UUID!) {
		getTeam(by: { id: $id }) {
			members {
				id
			}
		}
	}
`);

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const deleteTeamModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export const DeleteTeamModal: FC<z.infer<typeof deleteTeamModalSchema>> = ({ id, name }) => {
	const { openSnackbar } = useSnackbar();
	const { mutate: deleteTeam } = useApiMutation(deleteTeamMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deleteTeam.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.deleteTeam.errorMessage,
				openSnackbar,
			});
		},
	});
	const { data } = useApiQuery(getTeamQuery, { variables: { id } });
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});

	const requiresConfirmation = data?.getTeam ? data.getTeam.members.length > 0 : false;

	return (
		<>
			<ModalContent
				isDelete
				requiresConfirmation={requiresConfirmation}
				title={intl.formatMessage({ id: 'teams.delete.confirmation' }, { name })}
				description={''}
			>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={'50%'} alignSelf={'center'}>
						<form
							noValidate
							onSubmit={(e) => {
								e.preventDefault();
								if (form.formState.isValid) {
									deleteTeam({ teamId: id });
									closeModal();
								}
							}}
						>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteTeam({ teamId: id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
				disablePrimaryButton={requiresConfirmation ? !form.formState.isValid : false}
			/>
		</>
	);
};
